import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import './primitives.css';

export type CalloutIntent = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export interface CalloutProps {
  intent?: CalloutIntent;
  title?: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const DEFAULT_ICONS: Record<CalloutIntent, ReactNode> = {
  info: <DotIcon />,
  success: <DotIcon />,
  warning: <DotIcon />,
  danger: <DotIcon />,
  neutral: <DotIcon />,
};

function DotIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="3" fill="currentColor" />
    </svg>
  );
}

export function Callout({
  intent = 'neutral',
  title,
  icon,
  children,
  className,
  onDismiss,
  dismissLabel = 'Dismiss',
}: CalloutProps) {
  const classes = ['lm-callout', intent !== 'neutral' ? `lm-callout--${intent}` : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} role={intent === 'danger' || intent === 'warning' ? 'alert' : undefined}>
      <span className="lm-callout__icon">{icon ?? DEFAULT_ICONS[intent]}</span>
      <div className="lm-callout__body">
        {title && <div className="lm-callout__title">{title}</div>}
        {children}
      </div>
      {onDismiss && (
        <button
          type="button"
          className="lm-callout__dismiss"
          aria-label={dismissLabel}
          onClick={onDismiss}
        >
          <X size={14} aria-hidden />
        </button>
      )}
    </div>
  );
}
